import React from 'react';
import {createDiagramWsClient} from '@/api/diagramWs';
import {diagramsApi} from '@/api/projects';
import type {
	DiagramVersionNavigatePayload,
	ModelPatchDto,
	ModelPatchOp,
	DiagramWsInbound,
	CatalogChangeDto,
	DiagramCursorOutbound,
	DiagramRemoteCursor,
	DiagramLiveOutbound,
	DiagramLivePayload,
	DiagramLockOutbound,
	DiagramLockPayload,
	DiagramObjectLock,
	DiagramObjectKind,
	DiagramAckPayload,
} from '@/types/api';
import {getPatchDedupKey, mergePatchForDedupKey} from '@/utils/diagram/deduplicatePatches';
import {
	isDiagramPatchOutgoingBlocked,
	registerDiagramPatchQueueClear,
} from '@/utils/diagram/diagramPatchQueueHmr';
const SENT_PATCH_ID_TTL_MS = 5 * 60 * 1000; // 5 минут

const CURSOR_STALE_MS = 5000;
const CURSOR_PRUNE_MS = 1000;

export type DiagramVersionState = {
	structuralVersion: number;
	historyStackCursor: number;
	historyStackSize: number;
};

type EditorRef = React.RefObject<{
	applyRemotePatch?: (patch: ModelPatchDto) => void;
	applyVersionNavigate?: (payload: DiagramVersionNavigatePayload) => void;
	applyCatalogChange?: (changes: CatalogChangeDto[]) => void;
	applyLiveGeometry?: (live: DiagramLivePayload) => void;
	applyLockEvent?: (lock: DiagramLockPayload) => void;
	getModeler?: () => unknown;
} | null>;

/** Кэш id отправленных патчей: id -> время истечения. Свои эхо не применяем. */
function pruneAndHas(cache: Map<string, number>, id: string | null | undefined): boolean {
	if (!id) return false;
	const now = Date.now();
	for (const [k, expiry] of cache.entries()) {
		if (expiry <= now) cache.delete(k);
	}
	const expiry = cache.get(id);
	if (expiry == null) return false;
	if (expiry <= now) {
		cache.delete(id);
		return false;
	}
	return true;
}

function objectIdsFromPatches(patches: ModelPatchOp[]): string[] {
	return patches
		.map(p => p.identifier || p.node?.identifier || p.connection?.identifier)
		.filter((id): id is string => Boolean(id));
}

function toObjectLock(payload: DiagramLockPayload): DiagramObjectLock | null {
	if (!payload.objectId || !payload.clientSessionId || !payload.userId) return null;
	return {
		objectId: payload.objectId,
		kind: payload.kind === 'connection' ? 'connection' : 'node',
		clientSessionId: payload.clientSessionId,
		userId: payload.userId,
		userName: payload.userName || 'Участник',
		pendingPersist: Boolean(payload.pendingPersist),
		viewIndex: payload.viewIndex ?? null,
		viewId: payload.viewId ?? null,
	};
}

export function useDiagramWs(
	projectId: number,
	diagramId: number,
	loading: boolean,
	editorRef: EditorRef,
	diagramVersionRef: React.MutableRefObject<DiagramVersionState>,
	historyAppliedRef?: React.MutableRefObject<Set<string>>
) {
	const clientSessionIdRef = React.useRef<string | null>(null);
	const sentPatchIdsRef = React.useRef<Map<string, number>>(new Map());
	const persistObjectsRef = React.useRef<Map<string, string[]>>(new Map());
	const wsClientRef = React.useRef<ReturnType<typeof createDiagramWsClient> | null>(null);
	const pendingByKeyRef = React.useRef<Map<string, ModelPatchOp>>(new Map());
	const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const flushScheduledRef = React.useRef(false);
	const awaitingVersionAckRef = React.useRef(false);
	const flushPendingRef = React.useRef<(force?: boolean) => void>(() => {});
	const remoteCursorsRef = React.useRef<Map<string, DiagramRemoteCursor>>(new Map());
	const objectLocksRef = React.useRef<Map<string, DiagramObjectLock>>(new Map());
	const ignoreOwnAcquireRef = React.useRef<Set<string>>(new Set());
	const [remoteCursors, setRemoteCursors] = React.useState<DiagramRemoteCursor[]>([]);
	const [objectLocks, setObjectLocks] = React.useState<DiagramObjectLock[]>([]);
	const [clientSessionId, setClientSessionId] = React.useState<string | null>(null);

	const publishLocks = React.useCallback(() => {
		setObjectLocks(Array.from(objectLocksRef.current.values()));
	}, []);

	const releaseLocalLocks = React.useCallback(
		(objectIds: string[]) => {
			const ws = wsClientRef.current;
			const sessionId = clientSessionIdRef.current;
			if (!ws || !sessionId) return;
			objectIds.forEach(objectId => {
				ws.sendLock({
					action: 'release',
					objectId,
					clientSessionId: sessionId,
				});
			});
		},
		[]
	);

	const applyRemoteCursor = React.useCallback((cursor: DiagramWsInbound & {kind: 'cursor'}) => {
		const payload = cursor.cursor;
		if (!payload?.clientSessionId || payload.clientSessionId === clientSessionIdRef.current) {
			return;
		}
		const map = remoteCursorsRef.current;
		if (payload.left || payload.x == null || payload.y == null) {
			if (!map.has(payload.clientSessionId)) return;
			map.delete(payload.clientSessionId);
			setRemoteCursors(Array.from(map.values()));
			return;
		}
		if (!payload.userId) return;
		map.set(payload.clientSessionId, {
			clientSessionId: payload.clientSessionId,
			userId: payload.userId,
			userName: payload.userName,
			x: payload.x,
			y: payload.y,
			viewId: payload.viewId ?? null,
			updatedAt: Date.now(),
		});
		setRemoteCursors(Array.from(map.values()));
	}, []);

	const applyRemoteLive = React.useCallback((live: DiagramLivePayload) => {
		if (!live?.clientSessionId || live.clientSessionId === clientSessionIdRef.current) {
			return;
		}
		editorRef.current?.applyLiveGeometry?.(live);
	}, [editorRef]);

	const applyRemoteLock = React.useCallback(
		(lock: DiagramLockPayload) => {
			if (lock.action === 'snapshot') {
				objectLocksRef.current = new Map();
				(lock.locks ?? []).forEach(item => {
					if (item.clientSessionId === clientSessionIdRef.current) return;
					const next = toObjectLock(item);
					if (next) objectLocksRef.current.set(next.objectId, next);
				});
				publishLocks();
				editorRef.current?.applyLockEvent?.(lock);
				return;
			}
			if (lock.action === 'deny') {
				if (lock.clientSessionId === clientSessionIdRef.current) {
					editorRef.current?.applyLockEvent?.(lock);
				}
				return;
			}
			if (lock.action === 'release') {
				if (lock.objectId) {
					objectLocksRef.current.delete(lock.objectId);
					publishLocks();
				}
				editorRef.current?.applyLockEvent?.(lock);
				if (
					lock.clientSessionId !== clientSessionIdRef.current &&
					(lock.x != null || lock.y != null || lock.w != null || lock.h != null || lock.bendpoints)
				) {
					editorRef.current?.applyLiveGeometry?.({
						clientSessionId: lock.clientSessionId || '',
						objectId: lock.objectId || '',
						kind: (lock.kind as DiagramObjectKind) || 'node',
						viewIndex: lock.viewIndex,
						viewId: lock.viewId,
						x: lock.x,
						y: lock.y,
						w: lock.w,
						h: lock.h,
						bendpoints: lock.bendpoints,
					});
				}
				return;
			}
			if (lock.action === 'acquire') {
				if (lock.clientSessionId === clientSessionIdRef.current) {
					if (lock.objectId && ignoreOwnAcquireRef.current.has(lock.objectId)) {
						return;
					}
					editorRef.current?.applyLockEvent?.(lock);
					return;
				}
				if (lock.objectId) ignoreOwnAcquireRef.current.delete(lock.objectId);
				const next = toObjectLock(lock);
				if (next) {
					objectLocksRef.current.set(next.objectId, next);
					publishLocks();
				}
				editorRef.current?.applyLockEvent?.(lock);
			}
		},
		[editorRef, publishLocks]
	);

	const applyAck = React.useCallback(
		(ack: DiagramAckPayload) => {
			if (ack.clientSessionId && ack.clientSessionId !== clientSessionIdRef.current) {
				return;
			}
			if (ack.structuralVersion != null) {
				diagramVersionRef.current = {
					structuralVersion: ack.structuralVersion,
					historyStackCursor: ack.historyStackCursor ?? diagramVersionRef.current.historyStackCursor,
					historyStackSize: ack.historyStackSize ?? diagramVersionRef.current.historyStackSize,
				};
			}
			awaitingVersionAckRef.current = false;
			const objectIds = ack.clientPatchId ? persistObjectsRef.current.get(ack.clientPatchId) : undefined;
			if (ack.clientPatchId) persistObjectsRef.current.delete(ack.clientPatchId);
			if (ack.status === 'conflict') {
				handleConflictRefetchRef.current();
				if (objectIds?.length) releaseLocalLocks(objectIds);
				return;
			}
			if (objectIds?.length) releaseLocalLocks(objectIds);
			flushPendingRef.current();
		},
		[diagramVersionRef, releaseLocalLocks]
	);

	const handleConflictRefetch = React.useCallback(() => {
		awaitingVersionAckRef.current = false;
		clearPendingQueueBody(debounceTimerRef, pendingByKeyRef, flushScheduledRef);
		diagramsApi.get(projectId, diagramId).then(g => {
			if (!g.data?.modelJson) return;
			const d = g.data;
			diagramVersionRef.current = {
				structuralVersion: d.structuralVersion ?? diagramVersionRef.current.structuralVersion,
				historyStackCursor: d.historyStackCursor ?? diagramVersionRef.current.historyStackCursor,
				historyStackSize: d.historyStackSize ?? diagramVersionRef.current.historyStackSize,
			};
			editorRef.current?.applyVersionNavigate?.({
				diagramId,
				structuralVersion: diagramVersionRef.current.structuralVersion,
				historyStackCursor: diagramVersionRef.current.historyStackCursor,
				historyStackSize: d.historyStackSize ?? 1,
				modelJson: d.modelJson,
			});
		});
	}, [projectId, diagramId, diagramVersionRef, editorRef]);
	const handleConflictRefetchRef = React.useRef(handleConflictRefetch);
	handleConflictRefetchRef.current = handleConflictRefetch;

	const flushPending = React.useCallback((force = false) => {
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = null;
		}
		flushScheduledRef.current = false;
		if (import.meta.env.DEV && isDiagramPatchOutgoingBlocked()) {
			pendingByKeyRef.current = new Map();
			return;
		}
		if (!force && awaitingVersionAckRef.current) {
			debounceTimerRef.current = setTimeout(() => flushPendingRef.current(), 50);
			return;
		}
		const map = pendingByKeyRef.current;
		if (map.size === 0) return;
		const patches = Array.from(map.values());
		pendingByKeyRef.current = new Map();
		const clientPatchId = crypto.randomUUID();
		const persistIds = objectIdsFromPatches(patches);
		if (persistIds.length) persistObjectsRef.current.set(clientPatchId, persistIds);
		const ver = diagramVersionRef.current;
		const patchToSend: ModelPatchDto = {
			patches,
			clientPatchId,
			clientSessionId: clientSessionIdRef.current ?? undefined,
			expectedStructuralVersion: ver.structuralVersion,
			expectedHistoryStackCursor: ver.historyStackCursor,
		};
		const ws = wsClientRef.current;
		if (ws?.isConnected()) {
			awaitingVersionAckRef.current = true;
			sentPatchIdsRef.current.set(clientPatchId, Date.now() + SENT_PATCH_ID_TTL_MS);
			ws.sendPatch(patchToSend);
		} else {
			awaitingVersionAckRef.current = true;
			sentPatchIdsRef.current.set(clientPatchId, Date.now() + SENT_PATCH_ID_TTL_MS);
			diagramsApi.applyPatches(projectId, diagramId, patchToSend).then(res => {
				awaitingVersionAckRef.current = false;
				if (res.status === 409) {
					handleConflictRefetch();
					releaseLocalLocks(persistIds);
					return;
				}
				if (res.error) {
					console.warn('[DiagramEditor] applyPatches (REST fallback) failed:', res.error);
					releaseLocalLocks(persistIds);
					return;
				}
				const d = res.data;
				if (d?.structuralVersion != null) {
					diagramVersionRef.current = {
						structuralVersion: d.structuralVersion,
						historyStackCursor: d.historyStackCursor ?? 0,
						historyStackSize: d.historyStackSize ?? diagramVersionRef.current.historyStackSize,
					};
				}
				releaseLocalLocks(persistIds);
				flushPendingRef.current();
			});
		}
	}, [projectId, diagramId, diagramVersionRef, handleConflictRefetch, releaseLocalLocks]);
	flushPendingRef.current = flushPending;

	const scheduleFlush = React.useCallback(() => {
		if (flushScheduledRef.current) return;
		flushScheduledRef.current = true;
		queueMicrotask(() => flushPendingRef.current());
	}, []);

	const clearPendingQueue = React.useCallback(() => {
		clearPendingQueueBody(debounceTimerRef, pendingByKeyRef, flushScheduledRef);
	}, []);

	React.useEffect(() => {
		if (!import.meta.env.DEV) return () => {};
		return registerDiagramPatchQueueClear(clearPendingQueue);
	}, [clearPendingQueue]);

	React.useEffect(() => {
		if (!diagramId || loading) return;
		const sessionId = crypto.randomUUID();
		clientSessionIdRef.current = sessionId;
		setClientSessionId(sessionId);
		const client = createDiagramWsClient(diagramId);
		wsClientRef.current = client;
		const unsub = client.subscribe((msg: DiagramWsInbound) => {
			if (msg.kind === 'cursor') {
				applyRemoteCursor(msg);
				return;
			}
			if (msg.kind === 'live') {
				applyRemoteLive(msg.live);
				return;
			}
			if (msg.kind === 'lock') {
				applyRemoteLock(msg.lock);
				return;
			}
			if (msg.kind === 'ack') {
				applyAck(msg.ack);
				return;
			}
			if (msg.kind === 'navigate') {
				const nav = msg.navigate;
				diagramVersionRef.current = {
					structuralVersion: nav.structuralVersion,
					historyStackCursor: nav.historyStackCursor,
					historyStackSize: nav.historyStackSize,
				};
				awaitingVersionAckRef.current = false;
				editorRef.current?.applyVersionNavigate?.(nav);
				flushPendingRef.current();
				return;
			}
			if (msg.kind === 'catalog') {
				editorRef.current?.applyCatalogChange?.(msg.catalog);
				return;
			}
			if (msg.kind !== 'patch') return;
			const patch = msg.patch;
			if (msg.historyActionId) {
				if (msg.structuralVersion != null) {
					diagramVersionRef.current = {
						structuralVersion: msg.structuralVersion,
						historyStackCursor:
							msg.historyStackCursor ?? diagramVersionRef.current.historyStackCursor,
						historyStackSize:
							msg.historyStackSize ?? diagramVersionRef.current.historyStackSize,
					};
					awaitingVersionAckRef.current = false;
				}
				const seen = historyAppliedRef?.current;
				if (!seen?.has(msg.historyActionId)) {
					seen?.add(msg.historyActionId);
					editorRef.current?.applyRemotePatch?.(patch);
				}
				flushPendingRef.current();
				return;
			}
			/* Сначала версия из конверта — иначе своё эхо по clientSessionId отбрасываем до апдейта и undo шлёт stale expected → 409 */
			if (msg.structuralVersion != null) {
				diagramVersionRef.current = {
					structuralVersion: msg.structuralVersion,
					historyStackCursor:
						msg.historyStackCursor ?? diagramVersionRef.current.historyStackCursor,
					historyStackSize:
						msg.historyStackSize ?? diagramVersionRef.current.historyStackSize,
				};
				awaitingVersionAckRef.current = false;
			}
			if (
				patch?.clientSessionId != null &&
				patch.clientSessionId === clientSessionIdRef.current
			) {
				flushPendingRef.current();
				return;
			}
			if (pruneAndHas(sentPatchIdsRef.current, patch?.clientPatchId)) {
				flushPendingRef.current();
				return;
			}
			editorRef.current?.applyRemotePatch?.(patch);
			flushPendingRef.current();
		});
		const snapshotTimer = setTimeout(() => {
			client.sendLock({action: 'snapshot', clientSessionId: sessionId});
		}, 50);
		return () => {
			clearTimeout(snapshotTimer);
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
				debounceTimerRef.current = null;
			}
			flushPendingRef.current(true);
			awaitingVersionAckRef.current = false;
			client.sendCursor({clientSessionId: sessionId, left: true});
			unsub();
			client.disconnect();
			wsClientRef.current = null;
			remoteCursorsRef.current = new Map();
			objectLocksRef.current = new Map();
			setObjectLocks([]);
			setClientSessionId(null);
		};
	}, [diagramId, loading, editorRef, diagramVersionRef, historyAppliedRef, applyRemoteCursor, applyRemoteLive, applyRemoteLock, applyAck]);

	React.useEffect(() => {
		const timer = setInterval(() => {
			const map = remoteCursorsRef.current;
			if (map.size === 0) return;
			const now = Date.now();
			let changed = false;
			for (const [id, cursor] of map) {
				if (now - cursor.updatedAt > CURSOR_STALE_MS) {
					map.delete(id);
					changed = true;
				}
			}
			if (changed) setRemoteCursors(Array.from(map.values()));
		}, CURSOR_PRUNE_MS);
		return () => clearInterval(timer);
	}, []);

	const sendCursor = React.useCallback((cursor: DiagramCursorOutbound) => {
		const ws = wsClientRef.current;
		const sessionId = clientSessionIdRef.current;
		if (!ws || !sessionId) return;
		ws.sendCursor({
			...cursor,
			clientSessionId: sessionId,
		});
	}, []);

	const sendLive = React.useCallback((live: DiagramLiveOutbound) => {
		const ws = wsClientRef.current;
		const sessionId = clientSessionIdRef.current;
		if (!ws || !sessionId || !live?.objectId) return;
		ws.sendLive({
			...live,
			clientSessionId: sessionId,
		});
	}, []);

	const sendLock = React.useCallback((lock: DiagramLockOutbound) => {
		const ws = wsClientRef.current;
		const sessionId = clientSessionIdRef.current;
		if (!ws || !sessionId || !lock?.action) return;
		if (lock.action === 'release' && lock.objectId) {
			ignoreOwnAcquireRef.current.add(lock.objectId);
			if (objectLocksRef.current.delete(lock.objectId)) {
				publishLocks();
			}
			editorRef.current?.applyLockEvent?.({
				action: 'release',
				objectId: lock.objectId,
				kind: lock.kind,
				clientSessionId: sessionId,
			});
		}
		if (lock.action === 'acquire' && lock.objectId) {
			ignoreOwnAcquireRef.current.delete(lock.objectId);
		}
		ws.sendLock({
			...lock,
			clientSessionId: sessionId,
		});
	}, [editorRef, publishLocks]);

	const onApplyPatch = React.useCallback(
		(patch: {patches: ModelPatchOp[]}) => {
			if (!projectId || !diagramId || !patch?.patches?.length) return;
			if (import.meta.env.DEV && isDiagramPatchOutgoingBlocked()) return;
			const map = pendingByKeyRef.current;
			for (const p of patch.patches) {
				const key = getPatchDedupKey(p);
				const prev = map.get(key);
				map.set(
					key,
					prev && prev.op === p.op && p.op === 'updateNode' ? mergePatchForDedupKey(prev, p) : p
				);
			}
			scheduleFlush();
		},
		[projectId, diagramId, scheduleFlush]
	);

	return {
		onApplyPatch,
		sendCursor,
		sendLive,
		sendLock,
		remoteCursors,
		objectLocks,
		clientSessionId,
	};
}

function clearPendingQueueBody(
	debounceTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
	pendingByKeyRef: React.MutableRefObject<Map<string, ModelPatchOp>>,
	flushScheduledRef: React.MutableRefObject<boolean>
): void {
	if (debounceTimerRef.current) {
		clearTimeout(debounceTimerRef.current);
		debounceTimerRef.current = null;
	}
	flushScheduledRef.current = false;
	pendingByKeyRef.current = new Map();
}
